import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { Eye, Network, Search, TreePine, X } from "lucide-react";
import { AppDispatch, RootState } from "../../../../../store";
import { getNovaReferralNetwork } from "../../../../../Redux/referral/slice";
import Pagenation from "../../../../../ecommerce/globalcomponents/pagenation/Pagenation";
import NovaCommTeamTable from "./NovaCommTeamTable";
import {
  earningsStatusClass,
  statusLabel,
} from "../../earningsAdapters";
import "../../referralCommisson/SalarFlowPage.css";

const NovaCommissionTeam = () => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const orderNo = queryParams.get("orderNo") || "";

  const { novaReferralNetwork } = useSelector(
    (state: RootState) => state.referral,
  );

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (orderNo) {
      dispatch(
        getNovaReferralNetwork({
          orderNo,
          page,
          limit,
          status: "ALL",
          sortOrder: "DESC",
          search: debouncedSearch || undefined,
        }),
      );
    }
  }, [dispatch, orderNo, page, limit, debouncedSearch]);

  const networkData = novaReferralNetwork.data;
  const milestones = networkData?.network?.milestones || [];

  return (
    <section className="ri-page">
      <div className="ri-shell">
        <header className="ri-page-header">
          <div className="ri-page-title">
            <span className="ri-title-icon">
              <Network size={25} />
            </span>
            <div>
              <span className="ri-eyebrow">NOVA REFERRAL NETWORK</span>
              <h1>
                Nova Referral Network :{" "}
                {networkData?.orderDetails?.orderNo || orderNo || "—"}
              </h1>
              <p>
                Contribution requirement, active contributors and level
                incentives for this Nova network.
              </p>
            </div>
          </div>

          <div className="ri-header-actions">
            <a
              className="ri-outline-action"
              href={`/Tree?orderNo=${orderNo}&type=nova`}
            >
              <TreePine size={18} />
              View Nova Tree
            </a>
            <div className="ri-search ri-header-search-2">
              <Search size={19} />
              <input
                type="search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                placeholder="Search..."
                aria-label="Search network"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  aria-label="Clear search"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>
        </header>

        <div className="ri-toolbar">
          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
            aria-label="Entries per page"
          >
            {[10, 25, 50, 75, 100].map((val) => (
              <option value={val} key={val}>
                {val} Entries
              </option>
            ))}
          </select>
        </div>

        <NovaCommTeamTable milestones={milestones} orderNo={orderNo} />

        <div className="ri-mobile-list ri-nova-mobile-list">
          <a
            className="ri-mobile-tree-button"
            href={`/Tree?orderNo=${orderNo}&type=nova`}
          >
            <TreePine size={19} />
            Open Nova Referral Tree
          </a>
          {milestones.map((milestone, index) => (
            <article className="ri-nova-mobile-card" key={index}>
              <div className="ri-network-mobile-top">
                <div>
                  <span className="ri-mobile-label">NOVA MILESTONE</span>
                  <h2>
                    {milestone.milestoneName ||
                      `Milestone - ${milestone.milestoneNo || index + 1}`}
                  </h2>
                </div>
                <span className={earningsStatusClass(milestone.status)}>
                  {statusLabel(milestone.status)}
                </span>
              </div>
              <div className="ri-mobile-metric-grid">
                <div>
                  <span>Contribution Requirement</span>
                  <strong>{milestone.contributionRequirement}</strong>
                </div>
                <div>
                  <span>Active Contributors</span>
                  <strong>{milestone.activeContributors}</strong>
                </div>
              </div>
              <a
                className="ri-mobile-primary"
                href={`/NovalLevelTeam?orderNo=${orderNo}&milestoneNo=${milestone.milestoneNo || index + 1}`}
              >
                View Nova Level Incentives
                <Eye size={17} />
              </a>
            </article>
          ))}
        </div>

        <Pagenation
          currentPage={page}
          totalPages={networkData?.pagination?.totalPages || 0}
          onPageChange={setPage}
        />
      </div>
    </section>
  );
};

export default NovaCommissionTeam;
